import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as adminApi from '@/api/admin';
import type { AdminUserFormData, AdminUserFilters, AuditLogFilters } from '@/types/admin';

const USERS_KEY = ['admin', 'users'] as const;
const AUDIT_KEY = ['admin', 'audit'] as const;

/* === Users === */

export function useAdminUsers(filters?: AdminUserFilters) {
  return useQuery({
    queryKey: [...USERS_KEY, 'list', filters],
    queryFn: () => adminApi.getUsers(filters),
  });
}

export function useAdminUser(id: string | undefined) {
  return useQuery({
    queryKey: [...USERS_KEY, 'detail', id],
    queryFn: () => adminApi.getUser(id!),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AdminUserFormData) => adminApi.createUser(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdminUserFormData> }) =>
      adminApi.updateUser(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'detail', variables.id] });
      void queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'list'] });
    },
  });
}

export function useToggleUserActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => adminApi.toggleUserActive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (id: string) => adminApi.resetUserPassword(id),
  });
}

export function useUpdateUserRoles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      adminApi.updateUserRoles(id, roles),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUserScopes(id: string | undefined) {
  return useQuery({
    queryKey: [...USERS_KEY, 'scopes', id],
    queryFn: () => adminApi.getUserScopes(id!),
    enabled: !!id,
  });
}

export function useUpdateUserScopes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupIds, sectorIds }: { id: string; groupIds?: string[]; sectorIds?: string[] }) =>
      adminApi.updateUserScopes(id, {
        ...(groupIds !== undefined ? { group_ids: groupIds } : {}),
        ...(sectorIds !== undefined ? { sector_ids: sectorIds } : {}),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'scopes', variables.id] });
      void queryClient.invalidateQueries({ queryKey: [...USERS_KEY, 'list'] });
    },
  });
}

/* === Audit Log === */

export function useAuditLog(filters?: AuditLogFilters) {
  return useQuery({
    queryKey: [...AUDIT_KEY, 'list', filters],
    queryFn: () => adminApi.getAuditLog(filters),
  });
}

export function useAuditLogEntry(id: string | undefined) {
  return useQuery({
    queryKey: [...AUDIT_KEY, 'detail', id],
    queryFn: () => adminApi.getAuditLogEntry(id!),
    enabled: !!id,
  });
}

export function useAuditActions() {
  return useQuery({
    queryKey: [...AUDIT_KEY, 'actions'],
    queryFn: () => adminApi.getAuditActions(),
    staleTime: 5 * 60 * 1000,
  });
}
